

import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Paper
} from '@mui/material';
import { 
  AddCircleOutlineOutlined, 
  LinkOutlined,
  SearchOutlined 
} from '@mui/icons-material';
import { useReferenceData } from '../../hooks/useReferenceData';
import fieldService from '../../services/fieldService';
import { useAutomationMetadata } from '../../hooks/useAutomationMetadata';
import { autocompleteInputSx, tintSx } from '../../styles/wizardSx';

function ReferenceField({ field, value, onChange, onCreateNew, ...props }) {
  const { fetchReferenceData, loading } = useReferenceData();
  const { metadata } = useAutomationMetadata();
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');

  // Use your existing fieldService methods
  const endpoint = fieldService.getReferenceEndpoint(field, metadata);
  const labelField = fieldService.getLabelField(field, metadata);
  const isLoading = loading[endpoint];

  useEffect(() => {
    const loadOptions = async () => {
      if (!endpoint) return;
      
      try {
        const data = await fetchReferenceData(endpoint);
        if (data && Array.isArray(data)) {
          const formattedOptions = data.map(item => ({
            id: item.id || item._id,
            label: item[labelField] || item.name || item.title || 'Unknown'
          }));
          setOptions(formattedOptions);
        }
      } catch (error) {
        console.error('Error loading reference data:', error);
        setOptions([]);
      }
    };
    
    loadOptions();
  }, [endpoint, labelField, fetchReferenceData]);

  const canCreateNew = field.metadata?.allow_create !== false;
  const referenceTo = field.metadata?.reference_to || field.id.replace('_id', '');
  const recordType = referenceTo.charAt(0).toUpperCase() + referenceTo.slice(1);

  const optionsWithCreate = canCreateNew
    ? [
        { id: 'create_new', label: `Create new ${recordType}`, isCreateNew: true },
        ...options
      ]
    : options;

  const handleChange = (_, newValue) => {
    if (newValue?.isCreateNew && onCreateNew) {
      onCreateNew(referenceTo);
    } else {
      onChange(newValue?.id || '');
    }
  };

  // Find selected option
  const selectedOption = options.find(opt => opt.id === value) || null;

  return (
    <Autocomplete
      fullWidth
      options={optionsWithCreate}
      getOptionLabel={(option) => option.label || ''}
      value={selectedOption}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      sx={autocompleteInputSx}
      PaperComponent={({ children }) => (
        <Paper elevation={3} sx={{ borderRadius: '10px', mt: 1 }}>
          {children}
        </Paper>
      )}
      renderOption={(props, option) => (
        <Box 
          component="li" 
          {...props}
          sx={(theme) => ({
            '&:hover': option.isCreateNew ? tintSx(theme, 0.06) : { bgcolor: 'action.hover' }
          })}
        >
          {option.isCreateNew ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              width: '100%',
              py: 0.5
            }}>
              <AddCircleOutlineOutlined sx={{ mr: 1.5, color: 'primary.main', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                {option.label}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkOutlined sx={{ color: 'text.secondary', fontSize: 16 }} />
              <Typography variant="body2">{option.label}</Typography>
            </Box>
          )}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={field.label}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchOutlined sx={{ color: 'primary.main', fontSize: 18, ml: 1, mr: -0.5 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress 
                    size={18} 
                    sx={{ color: 'primary.main', mr: 1 }} 
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
          placeholder={isLoading ? 'Loading...' : `Search ${field.label}...`}
        />
      )}
      loading={isLoading}
      loadingText={
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          py: 2 
        }}>
          <CircularProgress size={20} sx={{ color: 'primary.main' }} />
          <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
            Loading options...
          </Typography>
        </Box>
      }
      noOptionsText={
        <Box sx={{ 
          textAlign: 'center', 
          py: 2,
          color: 'text.secondary'
        }}>
          <Typography variant="body2">
            No {field.label.toLowerCase()} found
          </Typography>
          {canCreateNew && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'primary.main' }}>
              Type to create new
            </Typography>
          )}
        </Box>
      }
      {...props}
    />
  );
}

export default ReferenceField;
